import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth, isFirebaseConfigured } from '../config/firebase';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { ensureUserDoc, readUserDoc, subscribeUserDoc, upsertUserDoc } from '../services/userService';
import { signUp, login, logout, loginWithGoogle, resetPassword } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDoc, setUserDoc] = useState(null);
  const unsubUserDocRef = useRef(null);
  const signingInRef = useRef(false);

  useEffect(() => {
    if (!isFirebaseConfigured || !auth) {
      setCurrentUser(null);
      setUserDoc(null);
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCurrentUser(null);
        setUserDoc(null);

        if (!signingInRef.current) {
          signingInRef.current = true;
          setLoading(true);
          try {
            await signInAnonymously(auth);
          } catch (error) {
            console.error('Anonymous sign-in error:', error);
            signingInRef.current = false;
            setLoading(false);
          }
        }
        return;
      }

      try {
        setCurrentUser(user);

        if (unsubUserDocRef.current) {
          unsubUserDocRef.current();
          unsubUserDocRef.current = null;
        }

        if (user?.uid) {
          await ensureUserDoc(user);
          unsubUserDocRef.current = subscribeUserDoc(
            user.uid,
            (docValue) => setUserDoc(docValue),
            () => setUserDoc(null)
          );
        } else {
          setUserDoc(null);
        }
      } catch (error) {
        console.error('Auth setup error:', error);
      } finally {
        signingInRef.current = false;
        setLoading(false);
      }
    });

    return () => {
      try {
        unsubscribe();
      } catch {
        // ignore
      }
      try {
        if (unsubUserDocRef.current) unsubUserDocRef.current();
      } catch {
        // ignore
      }
    };
  }, []);

  const updateUser = async (patch) => {
    if (!currentUser?.uid) throw new Error('Not signed in');
    await upsertUserDoc(currentUser.uid, patch);
  };

  const mergeAnonymousDocIntoUser = async (anonDoc, targetUser) => {
    if (!anonDoc || !targetUser?.uid) return;

    const patch = {};

    if (Array.isArray(anonDoc.cart) && anonDoc.cart.length > 0) {
      patch.cart = anonDoc.cart;
    }

    if (Array.isArray(anonDoc.wishlist) && anonDoc.wishlist.length > 0) {
      patch.wishlist = Array.from(new Set(anonDoc.wishlist.map(String)));
    }

    if (anonDoc.profile && typeof anonDoc.profile === 'object') {
      patch.profile = anonDoc.profile;
      if (typeof anonDoc.profile.email === 'string') patch.email = anonDoc.profile.email;
    }

    if (typeof anonDoc.currency === 'string') patch.currency = anonDoc.currency;
    if (typeof anonDoc.regionSelected === 'boolean') patch.regionSelected = anonDoc.regionSelected;
    if (Array.isArray(anonDoc.recentlyViewed)) patch.recentlyViewed = anonDoc.recentlyViewed;

    if (Object.keys(patch).length === 0) return;
    await upsertUserDoc(targetUser.uid, patch);
  };

  const signupWrapper = async (email, password, displayName) => {
    const anonymousUid = currentUser?.isAnonymous ? currentUser.uid : null;
    let anonDoc = null;
    try {
      if (anonymousUid) anonDoc = await readUserDoc(anonymousUid);
    } catch {
      anonDoc = null;
    }

    const user = await signUp(email, password, displayName);
    try {
      await ensureUserDoc(user);
    } catch {
      // ignore
    }

    try {
      const fullName = String(user?.displayName || displayName || '').trim();
      const parts = fullName.split(/\s+/).filter(Boolean);
      const firstName = parts[0] || '';
      const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';

      await upsertUserDoc(user.uid, {
        email: user.email || null,
        displayName: user.displayName || null,
        profile: {
          email: user.email || null,
          ...(firstName ? { firstName } : {}),
          ...(lastName ? { lastName } : {}),
        },
      });
    } catch {
      // ignore
    }
    try {
      if (anonDoc) await mergeAnonymousDocIntoUser(anonDoc, user);
    } catch (e) {
      console.error('Merge anonymous data failed:', e);
    }

    return user;
  };

  const loginWrapper = async (email, password) => {
    const anonymousUid = currentUser?.isAnonymous ? currentUser.uid : null;
    let anonDoc = null;
    try {
      if (anonymousUid) anonDoc = await readUserDoc(anonymousUid);
    } catch {
      anonDoc = null;
    }

    const user = await login(email, password);
    try {
      await ensureUserDoc(user);
    } catch {
      // ignore
    }

    try {
      const fullName = String(user?.displayName || '').trim();
      const parts = fullName.split(/\s+/).filter(Boolean);
      const firstName = parts[0] || '';
      const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';

      await upsertUserDoc(user.uid, {
        email: user.email || null,
        displayName: user.displayName || null,
        profile: {
          email: user.email || null,
          ...(firstName ? { firstName } : {}),
          ...(lastName ? { lastName } : {}),
        },
      });
    } catch {
      // ignore
    }
    try {
      if (anonDoc) await mergeAnonymousDocIntoUser(anonDoc, user);
    } catch (e) {
      console.error('Merge anonymous data failed:', e);
    }

    return user;
  };

  const value = {
    currentUser,
    userDoc,
    loading,
    signup: isFirebaseConfigured ? signupWrapper : null,
    login: isFirebaseConfigured ? loginWrapper : null,
    logout: isFirebaseConfigured ? logout : null,
    loginWithGoogle: isFirebaseConfigured ? loginWithGoogle : null,
    resetPassword: isFirebaseConfigured ? resetPassword : null,
    updateUser: isFirebaseConfigured ? updateUser : null,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};